import apiClient from '../../api/config/ApiClient';
import { AuthTokenManager } from '../TokenManager';
import { getRoleFromToken, normalizeUserRole } from '../utils/roleUtils';

/**
 * HTTP Status Codes for authentication
 */
const AUTH_HTTP_STATUS = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  BAD_REQUEST: 400
};

/**
 * Error types for authentication
 */
const AUTH_ERROR_TYPES = {
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  SESSION_CONFLICT: 'SESSION_CONFLICT',
  CONCURRENT_SESSION: 'CONCURRENT_SESSION',
  SESSION_TERMINATED: 'SESSION_TERMINATED',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};

/**
 * Handle session conflicts and concurrent session scenarios
 * @param {string} errorType - The type of session error
 * @param {string} message - Error message
 */
function handleSessionConflict(errorType, message) {
  console.log('🔍 AuthService: Handling session conflict:', { errorType, message });
  
  // Clear all local tokens
  AuthTokenManager.clearTokens();
  
  // Dispatch session conflict event for other components to handle
  window.dispatchEvent(new CustomEvent('auth:sessionConflict', {
    detail: { 
      errorType, 
      message,
      timestamp: new Date().toISOString()
    }
  }));
  
  // Dispatch logout event to clear user state
  window.dispatchEvent(new CustomEvent('auth:logout', {
    detail: { 
      success: false, 
      reason: 'session_conflict',
      message 
    }
  }));
}

/**
 * Enhanced error handler for authentication operations
 * @param {Error} error - The error object
 * @param {string} operation - The operation that failed
 * @returns {Object} Normalized error object
 */
function handleAuthError(error, operation = 'Authentication') {
  console.log('🔍 AuthService.handleAuthError: Function called with:', { error, operation });
  
  const status = error.response?.status;
  const errorData = error.response?.data || {};
  const detail = errorData.detail || errorData.message || error.message;
  
  console.log('🔍 AuthService: Processing error response:', {
    status,
    errorData,
    detail,
    type: errorData.type,
    description: errorData.description,
    title: errorData.title
  });
  
  let errorType = AUTH_ERROR_TYPES.UNKNOWN_ERROR;
  let message = `${operation} failed`;
  let recoverable = false;
  
  switch (status) {
    case AUTH_HTTP_STATUS.UNAUTHORIZED:
      if (detail?.toLowerCase().includes('invalid') || detail?.toLowerCase().includes('credentials')) {
        errorType = AUTH_ERROR_TYPES.INVALID_CREDENTIALS;
        message = 'Invalid email or password';
      } else if (detail?.toLowerCase().includes('expired') || detail?.toLowerCase().includes('token')) {
        errorType = AUTH_ERROR_TYPES.TOKEN_EXPIRED;
        message = 'Session expired. Please log in again.';
        recoverable = true;
      } else if (detail?.toLowerCase().includes('invalid or inactive token') ||
                 detail?.toLowerCase().includes('concurrent') || 
                 detail?.toLowerCase().includes('session') && 
                 (detail?.toLowerCase().includes('conflict') || detail?.toLowerCase().includes('terminated'))) {
        errorType = AUTH_ERROR_TYPES.SESSION_CONFLICT;
        message = 'Another session is active. Please log in again.';
        recoverable = true;
        // Handle session conflict immediately
        handleSessionConflict(errorType, message);
      } else if (detail?.toLowerCase().includes('inactive') || 
                 detail?.toLowerCase().includes('terminated') ||
                 detail?.toLowerCase().includes('revoked')) {
        errorType = AUTH_ERROR_TYPES.SESSION_TERMINATED;
        message = 'Session has been terminated. Please log in again.';
        recoverable = true;
        // Handle session termination immediately
        handleSessionConflict(errorType, message);
      } else {
        errorType = AUTH_ERROR_TYPES.TOKEN_INVALID;
        message = 'Invalid session. Please log in again.';
        recoverable = true;
      }
      break;
      
    case AUTH_HTTP_STATUS.FORBIDDEN:
      if (detail?.toLowerCase().includes('locked')) {
        errorType = AUTH_ERROR_TYPES.ACCOUNT_LOCKED;
        message = 'Account is locked. Please contact support.';
      } else {
        message = 'Access denied. You do not have permission to perform this action.';
      }
      break;
      
    case AUTH_HTTP_STATUS.UNPROCESSABLE_ENTITY:
      errorType = AUTH_ERROR_TYPES.VALIDATION_ERROR;
      message = detail || 'Validation failed. Please check your input.';
      recoverable = true;
      break;
      
    case AUTH_HTTP_STATUS.BAD_REQUEST:
      errorType = AUTH_ERROR_TYPES.VALIDATION_ERROR;
      message = detail || 'Invalid request. Please check your input.';
      recoverable = true;
      console.log('🔍 AuthService.handleAuthError: BAD_REQUEST case:', {
        status,
        errorData,
        detail,
        message,
        errorType
      });
      break;
      
    case AUTH_HTTP_STATUS.INTERNAL_SERVER_ERROR:
      errorType = AUTH_ERROR_TYPES.SERVER_ERROR;
      message = 'Server error occurred. Please try again later.';
      recoverable = true;
      break;
      
    default:
      if (!error.response) {
        errorType = AUTH_ERROR_TYPES.NETWORK_ERROR;
        message = 'Network error. Please check your connection.';
        recoverable = true;
      } else {
        message = detail || `${operation} failed`;
      }
  }
  
  const finalError = {
    message,
    status: status || 0,
    type: errorType,
    recoverable,
    data: errorData,
    originalError: error
  };
  
  console.log('🔍 AuthService.handleAuthError: Final error object:', finalError);
  
  return finalError;
}

class AuthService {
  static async register(payload) {
    try {
      const response = await apiClient.post('/auth/signup', payload, { skipAuth: true, minimalHeaders: true });
      
      // Check if response indicates an error (400, 500, etc.)
      if (response?.status >= 400) {
        throw new Error(response?.detail || response?.message || 'Registration failed');
      }
      
      const raw = response?.data || response || {};

      // Normalize backend response to app auth shape (same as login)
      const accessToken = raw.accessToken || raw.token || raw.jwt || null;
      const refreshToken = raw.refreshToken || null;

      let expiresInSeconds = null;
      if (typeof raw.expiresIn === 'number') {
        // Backend may return ms; convert to seconds if needed
        expiresInSeconds = raw.expiresIn > 100000 ? Math.floor(raw.expiresIn / 1000) : raw.expiresIn;
      } else if (raw.expiresAt) {
        const ms = new Date(raw.expiresAt).getTime() - Date.now();
        expiresInSeconds = ms > 0 ? Math.floor(ms / 1000) : 0;
      }

      const user = raw.user || {
        name: raw.fullName || raw.name || undefined,
        subscriptionValidTo: raw.subscriptionValidTo || undefined
      };

      // Prefer explicit role from login/signup response; fall back to JWT claims
      user.role = normalizeUserRole(
        user.role || raw.role || (accessToken ? getRoleFromToken(accessToken) : null)
      );

      const normalized = { accessToken, refreshToken, expiresIn: expiresInSeconds || 1800, user };

      return { data: normalized, error: null };
    } catch (error) {
      const authError = handleAuthError(error, 'Registration');
      return { data: null, error: authError };
    }
  }

  static async login(payload) {
    try {
      console.log('🔍 AuthService.login: Making request with payload:', payload);
      const response = await apiClient.post('/auth/login', payload, { skipAuth: true, minimalHeaders: true });
      console.log('🔍 AuthService.login: Raw response:', response);
      const raw = response?.data || response || {};

      // Normalize backend response to app auth shape
      const accessToken = raw.accessToken || raw.token || raw.jwt || null;
      const refreshToken = raw.refreshToken || null;

      let expiresInSeconds = null;
      if (typeof raw.expiresIn === 'number') {
        // Backend may return ms; convert to seconds if needed
        expiresInSeconds = raw.expiresIn > 100000 ? Math.floor(raw.expiresIn / 1000) : raw.expiresIn;
      } else if (raw.expiresAt) {
        const ms = new Date(raw.expiresAt).getTime() - Date.now();
        expiresInSeconds = ms > 0 ? Math.floor(ms / 1000) : 0;
      }

      const user = raw.user || {
        name: raw.fullName || raw.name || undefined,
        subscriptionValidTo: raw.subscriptionValidTo || undefined
      };

      // Prefer explicit role from login response; fall back to JWT claims
      user.role = normalizeUserRole(
        user.role || raw.role || (accessToken ? getRoleFromToken(accessToken) : null)
      );

      const normalized = { accessToken, refreshToken, expiresIn: expiresInSeconds || 1800, user };

      return { data: normalized, error: null };
    } catch (error) {
      const authError = handleAuthError(error, 'Login');
      return { data: null, error: authError };
    }
  }

  static async forgotPassword(email) {
    try {
      const response = await apiClient.post('/auth/forgot-password', { email }, { skipAuth: true, minimalHeaders: true });
      
      // Check if response indicates an error (400, 500, etc.)
      if (response?.status >= 400) {
        throw new Error(response?.detail || response?.message || 'Password reset request failed');
      }
      
      return { data: response?.data || response || { success: true }, error: null };
    } catch (error) {
      const authError = handleAuthError(error, 'Password reset request');
      return { data: null, error: authError };
    }
  }

  static async resetPassword(token, newPassword) {
    try {
      // Send token both as query parameter AND in request body
      const url = `/auth/reset-password?token=${encodeURIComponent(token)}`;
      const body = { token, newPassword };
      
      console.log('🔍 AuthService.resetPassword: Sending request to:', url);
      console.log('🔍 AuthService.resetPassword: Request body:', body);
      
      const response = await apiClient.post(url, body, { skipAuth: true, minimalHeaders: true });
      
      // Check if response indicates an error (400, 500, etc.)
      if (response?.status >= 400) {
        throw new Error(response?.detail || response?.message || 'Password reset failed');
      }
      
      return { data: response?.data || response || { success: true }, error: null };
    } catch (error) {
      const authError = handleAuthError(error, 'Password reset');
      return { data: null, error: authError };
    }
  }

  static async logout() {
    try {
      console.log('🔍 AuthService: Starting logout process');
      // Call backend logout endpoint to invalidate server-side session
      console.log('🔍 AuthService: Making POST request to /auth/logout');
      const response = await apiClient.post('/auth/logout', {}, { skipAuth: false });
      console.log('🔍 AuthService: Logout response received:', response);
      
      return { data: response?.data || { success: true }, error: null };
    } catch (error) {
      // Even if backend logout fails, we should still clear local tokens
      console.warn('Backend logout failed, but clearing local tokens:', error.message);
      console.log('🔍 AuthService: Logout error details:', error);
      return { 
        data: { success: true, localOnly: true }, 
        error: null 
      };
    }
  }

  static async refreshToken(refreshToken) {
    try {
      const response = await apiClient.post('/auth/refresh', 
        { refreshToken }, 
        { skipAuth: true, minimalHeaders: true }
      );
      
      const raw = response?.data || response || {};
      
      // Normalize backend response to app auth shape
      const accessToken = raw.accessToken || raw.token || raw.jwt || null;
      const newRefreshToken = raw.refreshToken || refreshToken; // Use new refresh token or keep current
      
      let expiresInSeconds = null;
      if (typeof raw.expiresIn === 'number') {
        expiresInSeconds = raw.expiresIn > 100000 ? Math.floor(raw.expiresIn / 1000) : raw.expiresIn;
      } else if (raw.expiresAt) {
        const ms = new Date(raw.expiresAt).getTime() - Date.now();
        expiresInSeconds = ms > 0 ? Math.floor(ms / 1000) : 0;
      }

      const user = raw.user || null;

      const normalized = { 
        accessToken, 
        refreshToken: newRefreshToken, 
        expiresIn: expiresInSeconds || 1800, 
        user 
      };

      return { data: normalized, error: null };
    } catch (error) {
      const authError = handleAuthError(error, 'Token refresh');
      return { data: null, error: authError };
    }
  }

  static async getUserProfile() {
    try {
      const response = await apiClient.get('/auth/profile');
      
      return { data: response?.data || response, error: null };
    } catch (error) {
      const authError = handleAuthError(error, 'Get user profile');
      return { data: null, error: authError };
    }
  }

  static async updateUserProfile(profileData) {
    try {
      const response = await apiClient.put('/auth/profile', profileData);
      
      return { data: response?.data || response, error: null };
    } catch (error) {
      const authError = handleAuthError(error, 'Update user profile');
      return { data: null, error: authError };
    }
  }

  static async changePassword(currentPassword, newPassword) {
    try {
      const response = await apiClient.post('/auth/change-password', {
        currentPassword,
        newPassword
      });
      
      return { data: response?.data || response || { success: true }, error: null };
    } catch (error) {
      const authError = handleAuthError(error, 'Change password');
      return { data: null, error: authError };
    }
  }

  static async validateSession() {
    try {
      const response = await apiClient.get('/auth/validate');
      
      return { data: response?.data || response, error: null };
    } catch (error) {
      const authError = handleAuthError(error, 'Session validation');
      return { data: null, error: authError };
    }
  }

  static async deleteAccount(password) {
    try {
      const response = await apiClient.delete('/auth/account', {
        data: { password }
      });
      
      return { data: response?.data || response || { success: true }, error: null };
    } catch (error) {
      const authError = handleAuthError(error, 'Delete account');
      return { data: null, error: authError };
    }
  }

  /**
   * Check if a token is expired
   * @param {string} token - JWT token to check
   * @returns {boolean} True if token is expired
   */
  static isTokenExpired(token) {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch (error) {
      console.warn('Invalid token format:', error);
      return true;
    }
  }

  /**
   * Get token expiration time
   * @param {string} token - JWT token
   * @returns {number|null} Expiration timestamp or null if invalid
   */
  static getTokenExpiration(token) {
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000; // Convert to milliseconds
    } catch (error) {
      console.warn('Invalid token format:', error);
      return null;
    }
  }

  /**
   * Get time until token expires in seconds
   * @param {string} token - JWT token
   * @returns {number} Seconds until expiration (negative if expired)
   */
  static getTimeUntilExpiration(token) {
    const expiration = this.getTokenExpiration(token);
    if (!expiration) return -1;
    
    return Math.floor((expiration - Date.now()) / 1000);
  }

  /**
   * Check if token needs refresh (expires within 5 minutes)
   * @param {string} token - JWT token
   * @returns {boolean} True if token needs refresh
   */
  static needsRefresh(token) {
    const timeUntilExpiration = this.getTimeUntilExpiration(token);
    return timeUntilExpiration > 0 && timeUntilExpiration < 300; // 5 minutes
  }

  /**
   * Auto-refresh token if needed
   * @param {string} refreshToken - Refresh token
   * @returns {Promise<Object>} New token data or error
   */
  static async autoRefreshToken(refreshToken) {
    if (!refreshToken) {
      return { 
        data: null, 
        error: { 
          message: 'No refresh token available', 
          type: AUTH_ERROR_TYPES.TOKEN_INVALID 
        } 
      };
    }

    return await this.refreshToken(refreshToken);
  }

  /**
   * Validate current session and refresh if needed
   * @param {string} accessToken - Current access token
   * @param {string} refreshToken - Current refresh token
   * @returns {Promise<Object>} Valid token data or error
   */
  static async validateAndRefreshSession(accessToken, refreshToken) {
    // If no access token, try to refresh
    if (!accessToken) {
      return await this.autoRefreshToken(refreshToken);
    }

    // If token is expired, try to refresh
    if (this.isTokenExpired(accessToken)) {
      return await this.autoRefreshToken(refreshToken);
    }

    // If token needs refresh soon, proactively refresh
    if (this.needsRefresh(accessToken)) {
      return await this.autoRefreshToken(refreshToken);
    }

    // Token is valid, return current data
    return { 
      data: { 
        accessToken, 
        refreshToken, 
        expiresIn: this.getTimeUntilExpiration(accessToken),
        user: null // Will be populated by caller
      }, 
      error: null 
    };
  }

  /**
   * Change user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<{data: any, error: any}>}
   */
  static async changePassword(currentPassword, newPassword) {
    try {
      console.log('🔍 AuthService.changePassword: Making request to /auth/change-password');
      const response = await apiClient.post('/auth/change-password', {
        currentPassword,
        newPassword
      });

      console.log('🔍 AuthService.changePassword: Success response:', response);
      const raw = response?.data || response || {};
      
      return { 
        data: raw, 
        error: null 
      };
    } catch (error) {
      // Check if this is a validation error with detail field
      if (error.response?.status === 400 && error.response?.data?.detail) {
        // Directly return the error with the detail message in the expected format
        const directError = {
          message: error.response.data.detail,
          status: error.response.status,
          type: 'VALIDATION_ERROR',
          recoverable: true,
          data: error.response.data,
          originalError: error
        };
        return { data: null, error: directError };
      }
      
      const result = this.handleAuthError(error, 'changePassword');
      return { data: null, error: result };
    }
  }
}

export default AuthService;


